export interface AudioSettings {
  readonly music: boolean;
  readonly sfx: boolean;
}

export class ProceduralAudio {
  private context: AudioContext | null = null;
  private ambient: OscillatorNode | null = null;
  private ambientGain: GainNode | null = null;

  async unlock(settings: AudioSettings): Promise<void> {
    if (!this.context) this.context = new AudioContext();
    if (this.context.state === "suspended") await this.context.resume();
    this.apply(settings);
  }

  apply(settings: AudioSettings): void {
    if (!this.context) return;
    if (settings.music && !this.ambient) {
      const oscillator = this.context.createOscillator();
      const gain = this.context.createGain();
      oscillator.type = "sine";
      oscillator.frequency.value = 46.25;
      gain.gain.value = 0.018;
      oscillator.connect(gain).connect(this.context.destination);
      oscillator.start();
      this.ambient = oscillator;
      this.ambientGain = gain;
    }
    if (this.ambientGain) {
      this.ambientGain.gain.setTargetAtTime(
        settings.music ? 0.018 : 0,
        this.context.currentTime,
        0.1,
      );
    }
  }

  cue(
    kind: "pickup" | "boost" | "tide" | "death",
    settings: AudioSettings,
  ): void {
    if (!settings.sfx || !this.context) return;
    const oscillator = this.context.createOscillator();
    const gain = this.context.createGain();
    const now = this.context.currentTime;
    const frequencies = {
      pickup: 440,
      boost: 116,
      tide: 220,
      death: 74,
    } as const;
    oscillator.type =
      kind === "death" ? "sawtooth" : kind === "boost" ? "square" : "sine";
    oscillator.frequency.setValueAtTime(frequencies[kind], now);
    oscillator.frequency.exponentialRampToValueAtTime(
      Math.max(32, frequencies[kind] * (kind === "death" ? 0.4 : 1.6)),
      now + 0.16,
    );
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.06, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.18);
    oscillator.connect(gain).connect(this.context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.2);
  }

  stop(): void {
    this.ambient?.stop();
    this.ambient = null;
    this.ambientGain = null;
  }
}
